import type { GccOutsourcingScenario, GccPathway, Project } from '@shared/types'
import { GCC_PATHWAYS, GCC_PATHWAY_LABELS, GCC_WAVES, GCC_WAVE_BANDS, waveToVerdict } from '@tn/shared'
import { combinedDashboardLabel } from '@shared/gcc/combine'

export interface GccWizardStep {
  index: number
  title: string
  description: string
}

/** Steps shown in the GCC wizard navigation. Step index matches the URL param. */
export const GCC_WIZARD_STEPS: readonly GccWizardStep[] = [
  { index: 1, title: 'Function Scope', description: 'Define which functions and sub-functions to include in the GCC analysis.' },
  { index: 2, title: 'Eligibility Rules', description: 'Configure rules to classify which tasks are eligible for GCC migration.' },
  { index: 3, title: 'Pay Rate Table', description: 'Set blended pay rates for each sourcing tier and geography.' },
  { index: 4, title: 'Branding', description: 'Customise client colours and contact details for the output.' },
  { index: 5, title: 'Generate', description: 'Run the five-step GCC AI pipeline and download your results.' },
]

/** Labels for each step of the AI pipeline (matches GccProgressEvent.step). Shared with the server (WI 2323). */
export { GCC_STEP_LABELS } from '@tn/shared'

/** Number of AI pipeline steps that contribute to the progress bar (steps 1–4). */
export const GCC_PIPELINE_STEP_COUNT = 4

export const GCC_SCENARIO_LABELS: Record<GccOutsourcingScenario, string> = {
  High: 'Aggressive Outsourcing',
  Medium: 'Moderate Outsourcing',
  Low: 'Conservative Outsourcing',
  Retained: 'Retained (HQ + Markets) Baseline',
}

/**
 * Label of a GCC TN analysis in the combined report (WI 2486), in the wording of its
 * dashboard hero (gccScenarioLabel in @tn/core): "Combined: Retained + Low",
 * "Retained (HQ + Markets) Baseline" or "Moderate Outsourcing (Medium)".
 * Falls back to "TN Analysis <n>" (1-based position) when the scenario is not known.
 */
export function gccTnAnalysisLabel(
  tnProject: Pick<Project, 'gccScenario' | 'gccCombined'> | undefined,
  idx: number,
): string {
  if (tnProject?.gccCombined) return combinedDashboardLabel(tnProject.gccCombined.offshoreScenario)
  const scenario = tnProject?.gccScenario
  if (!scenario) return `TN Analysis ${idx + 1}`
  const label = GCC_SCENARIO_LABELS[scenario] as string | undefined
  if (!label) return scenario
  return scenario === 'Retained' ? label : `${label} (${scenario})`
}

export const GCC_SCENARIO_DESCRIPTIONS: Record<GccOutsourcingScenario, string> = {
  High: 'All GCC-eligible roles move offshore. Only HQ/Markets-designated work stays onshore.',
  Medium: '80% of GCC-eligible roles move offshore. Some eligible work stays onshore during transition.',
  Low: '60% of GCC-eligible roles move offshore. A larger onshore footprint is retained.',
  Retained: 'HQ and Markets roles that stay onshore in every scenario, analysed as a standalone baseline.',
}

/** Enum descriptions used in TipEnum tooltip legends. */
export const TIER_META: [string, string][] = [
  ['HQ', 'Onshore headquarters — senior leadership and high-touch client-facing roles.'],
  ['Markets', 'Regional market presence — locally-embedded roles that cannot move offshore.'],
  ['GCC-Offshore', 'Offshore GCC delivery — primary target for cost optimisation.'],
  ['GCC-Nearshore', 'Nearshore GCC delivery — lower latency, often used for higher-touch functions.'],
  ['MSP', 'Managed Service Provider — specialist work contracted externally.'],
]

/** Step 2 ownership options (WI 2324). MSP is who does the work, not where. */
export const OWNERSHIP_META: [string, string][] = [
  ['Captive GCC', 'The client’s own Global Capability Center — nearshore or offshore.'],
  ['MSP', 'Managed Service Provider — work contracted externally, from any location.'],
  ['Retained', 'Stays with the client’s onshore organisation — HQ or Markets.'],
]

/** Step 2 delivery locations (WI 2324). */
export const LOCATION_META: [string, string][] = [
  ['HQ', 'Home-country headquarters.'],
  ['Markets', 'Regional or local market presence.'],
  ['Nearshore', 'Nearby lower-cost location with time-zone overlap (e.g. Krakow, Guadalajara).'],
  ['Offshore', 'Distant lower-cost location (e.g. Bangalore, Manila).'],
]

export const EXISTENCE_VERDICT_META: [string, string][] = [
  ['Keep', 'Task continues in the future model with no structural change.'],
  ['Redesign', 'Task continues but the way-of-working must be redesigned before or during transition.'],
  ['Eliminate', 'Task is discontinued — no longer needed in the future operating model.'],
  ['Automate', 'Task is end-to-end automated and removed from the human workforce.'],
]

/** Verdicts share the wave time bands (WI 2325): Now = Wave 1, Next = Wave 2, Later = Wave 3. */
export const VERDICT_META: [string, string][] = [
  ...GCC_WAVES.map((w): [string, string] => [waveToVerdict(w), `Initiate in ${GCC_WAVE_BANDS[w].label} (Wave ${w}).`]),
  ['Never', 'Not recommended for transition in this planning horizon.'],
]

export const WAVE_META: [string, string][] = GCC_WAVES.map((w): [string, string] => [String(w), `Wave ${w} — ${GCC_WAVE_BANDS[w].label}.`])

const PATHWAY_DESCRIPTIONS: Record<GccPathway, string> = {
  TransformFirst: 'AI is applied before the move, so only the residual work migrates.',
  ShiftFirst: 'The work moves as it is today, then transforms at its new owner.',
  ShiftAndTransform: 'The work moves and transforms in the same wave.',
  RetainAndTransform: 'The work stays with the retained organisation; AI is applied in place.',
  ShiftOnly: 'Human work that moves, with no AI transformation.',
  RetainAsIs: 'Human work that stays where it is, with no change.',
}

/** Step 3 pathways (WI 2325), in display order. */
export const PATHWAY_META: [string, string][] = GCC_PATHWAYS.map((p): [string, string] => [GCC_PATHWAY_LABELS[p], PATHWAY_DESCRIPTIONS[p]])

export const SEGMENT_META: [string, string][] = [
  ['GCC-eligible', 'Work that moves to the GCC delivery model.'],
  ['Retained', 'Work that stays onshore in HQ or Markets.'],
]

export const RTAG_META: [string, string][] = [
  ['Retained', 'Role continues unchanged in the end-state model.'],
  ['Evolved', 'Role persists but requires reskilling for new automation-augmented tasks.'],
  ['Consolidated', 'Multiple existing roles merge into one broader end-state role.'],
  ['New', 'Net-new capability that does not exist in the current workforce.'],
]
